import React from "react";
import Tooltip from "@mui/material/Tooltip";
// Per-type icons — the same ones used in the Create Training Content sidebar.
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Shared structural pieces for the track matrices (Track Status Matrix by
// employee, and the Location x Track "Assignment Completion" matrix). These
// own the awkward bits that are identical across both grids: responsive column
// sizing, the diagonal <-> horizontal header switch, and the frozen first
// column's scroll shadow. Row and cell content stay with each call site.

// Track columns flex to fill the available width, clamped between a dense
// square minimum and a comfortable maximum. Once a column is wide enough to fit
// a horizontal label we drop the diagonal header (it only earns its awkwardness
// when columns are too narrow for horizontal text). When columns hit the
// minimum the table overflows and scrolls.
export const STICKY_COL_WIDTH = 240;
export const MIN_TRACK_COL = 52;
export const MAX_TRACK_COL = 220;
export const HORIZONTAL_HEADER_MIN = 120;
// Reserve a couple of pixels so the columns never sum to exactly the measured
// width. clientWidth is an integer while the real layout width is fractional,
// so without this slack the table can render ~1px past the container and trip a
// hairline horizontal scroll. The auto filler column soaks up the gutter.
const FILL_GUTTER = 2;

// Measure the visible matrix width so track columns can flex to fill it. A
// callback ref (rather than useRef + useEffect) keeps this correct when the
// matrix mounts after data loads or on tab switches.
export function useTrackColumnSizing(columnCount) {
  const [matrixWidth, setMatrixWidth] = React.useState(0);
  const resizeObserverRef = React.useRef(null);
  const matrixScrollRef = React.useCallback((node) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (!node) return;
    setMatrixWidth(node.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      setMatrixWidth((prev) => (prev === width ? prev : width));
    });
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  const trackColumnWidth = (() => {
    if (!matrixWidth || !columnCount) return MIN_TRACK_COL;
    const available = matrixWidth - STICKY_COL_WIDTH - FILL_GUTTER;
    const raw = available / columnCount;
    // Columns flex to fill the row, clamped between a dense minimum and a
    // comfortable maximum so a single track doesn't stretch into one enormous
    // bar. Below the max, columns fill the width (the auto filler stays ~0);
    // above the minimum the matrix overflows and scrolls. Floor (not round) so N
    // columns can never overshoot the available width and trip a hairline scroll.
    return Math.max(MIN_TRACK_COL, Math.min(MAX_TRACK_COL, Math.floor(raw)));
  })();
  const useHorizontalHeader = trackColumnWidth >= HORIZONTAL_HEADER_MIN;

  return { matrixScrollRef, matrixWidth, trackColumnWidth, useHorizontalHeader };
}

// Right-edge shadow on the frozen first column once the matrix is scrolled
// horizontally, signaling there's content sliding underneath it. Rendered as an
// inline gradient strip that translates just past the cell's right edge. Inline
// styling is deterministic (no reliance on Tailwind emitting arbitrary
// utilities) and a gradient on a normal box paints regardless of the table's
// border-collapse mode. The sticky cells carry no horizontal padding (it lives
// on their inner content), so the cell's padding box reaches the true edge.
export function useStickyEdgeShadow() {
  const [isMatrixScrolled, setIsMatrixScrolled] = React.useState(false);
  // Returning the same value short-circuits React's re-render, so this only
  // costs a render at the 0 <-> non-0 boundary, not on every scroll tick.
  const handleMatrixScroll = (e) => {
    const scrolled = e.currentTarget.scrollLeft > 0;
    setIsMatrixScrolled((prev) => (prev === scrolled ? prev : scrolled));
  };
  const renderStickyEdgeShadow = () =>
    isMatrixScrolled ? (
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "12px",
          transform: "translateX(100%)",
          background:
            "linear-gradient(to right, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0))",
          pointerEvents: "none",
        }}
      />
    ) : null;

  return { isMatrixScrolled, handleMatrixScroll, renderStickyEdgeShadow };
}

// The <thead>'s label row, shared by both matrices: the frozen first-column
// header (stickyLabel) followed by one header per track column. Columns wide
// enough for horizontal text get a normal header; narrower columns get the
// diagonal (-45deg) label with hand-built clip-path triangle + rotated
// dividers. A trailing filler <th> stretches so the header slate and row
// borders reach full width while the square cells stay fixed.
//
// Props:
//   columns               [{ uuid, name, ... }]
//   trackColumnWidth      px width for each track column
//   useHorizontalHeader   bool from useTrackColumnSizing
//   stickyLabel           text for the frozen first column ("Team Member" / "Location")
//   renderStickyEdgeShadow from useStickyEdgeShadow
//   renderColumnMeta(col) optional node for the small sub-label under/beside the name
// Short label for a column's assignment type. NULL/"track" returns null (a
// normal authored track gets no badge — only the new single-asset direct
// assignments are flagged, keeping the dense matrix clean).
const ASSIGNMENT_TYPE_LABELS = {
  module: "Module",
  section: "Section",
  quiz: "Quiz",
  swipe_deck: "Deck",
  form: "Form",
};

export function assignmentTypeLabel(assignmentType) {
  return ASSIGNMENT_TYPE_LABELS[assignmentType] || null;
}

// Per-type icon component (same icons as the Create Training Content sidebar).
// NULL/"track" returns null — normal tracks aren't badged.
const ASSIGNMENT_TYPE_ICONS = {
  module: ViewModuleIcon,
  section: ViewHeadlineIcon,
  quiz: QuizOutlinedIcon,
  swipe_deck: StyleOutlinedIcon,
  form: DescriptionOutlinedIcon,
};

export function assignmentTypeIcon(assignmentType) {
  return ASSIGNMENT_TYPE_ICONS[assignmentType] || null;
}

export function TrackMatrixHeaderRow({
  columns,
  trackColumnWidth,
  useHorizontalHeader,
  stickyLabel,
  renderStickyEdgeShadow,
  renderColumnMeta,
  // Optional: when provided, track labels become clickable (and keyboard
  // focusable) and call onTrackClick(track). Off by default so the Location x
  // Track matrix keeps its read-only headers.
  onTrackClick,
}) {
  // Props that make a label behave as a button when onTrackClick is set.
  const clickableProps = (track) =>
    onTrackClick
      ? {
          role: "button",
          tabIndex: 0,
          onClick: () => onTrackClick(track),
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onTrackClick(track);
            }
          },
        }
      : {};
  return (
    <tr>
      {/* Pin the frozen column to an exact width. Under table-fixed the column
          widths are taken from this header row, so the explicit width here (and
          the floored track widths) makes the matrix total exactly the container
          width — no auto-layout stretching/rounding that would force a scroll. */}
      <th
        className="sticky left-0 z-30 bg-slate-50 py-4 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-slate-500"
        style={{ width: STICKY_COL_WIDTH }}
      >
        <div className="px-5">{stickyLabel}</div>
        {renderStickyEdgeShadow && renderStickyEdgeShadow()}
      </th>
      {columns.map((track, trackIndex) => {
        const isLastTrack = trackIndex === columns.length - 1;
        const meta = renderColumnMeta ? renderColumnMeta(track) : null;
        const typeLabel = assignmentTypeLabel(track.assignment_type);
        const TypeIcon = assignmentTypeIcon(track.assignment_type);

        // Wide enough for a horizontal label: render a normal column header
        // instead of the diagonal one.
        if (useHorizontalHeader) {
          return (
            <th
              key={track.uuid}
              className="border-l border-slate-200 px-2 py-4 align-bottom"
              style={{ width: trackColumnWidth, minWidth: trackColumnWidth }}
            >
              <Tooltip
                title={
                  onTrackClick
                    ? `Assign "${track.name}" to team members`
                    : track.name
                }
                arrow
              >
                <div
                  className={`mx-auto ${
                    onTrackClick
                      ? "group cursor-pointer rounded-lg px-1.5 py-1 transition-colors hover:bg-violet-50"
                      : ""
                  }`}
                  {...clickableProps(track)}
                >
                  <p
                    className={`line-clamp-2 text-center text-xs font-semibold leading-snug text-slate-700 ${
                      onTrackClick ? "group-hover:text-violet-700" : ""
                    }`}
                  >
                    {track.name}
                  </p>
                  {typeLabel && (
                    <p className="mt-1 text-center">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-xxs font-semibold text-emerald-700">
                        {TypeIcon && <TypeIcon style={{ fontSize: 12 }} />}
                        {typeLabel}
                      </span>
                    </p>
                  )}
                  {meta != null && (
                    <p className="mt-1 text-center text-xxs font-medium text-slate-400">
                      {meta}
                    </p>
                  )}
                </div>
              </Tooltip>
            </th>
          );
        }

        return (
          <th
            key={track.uuid}
            className="relative h-32 p-0 align-bottom"
            // Earlier (left) labels paint over their right neighbors so a label
            // that overflows into the next cell stays fully hoverable. Kept
            // below the sticky first column (z-30).
            style={{
              width: trackColumnWidth,
              minWidth: trackColumnWidth,
              zIndex: columns.length - trackIndex,
            }}
          >
            <div className="relative h-full w-full">
              {/* Extend the header slate past the last column as a triangle
                  whose hypotenuse is parallel to the labels (a clean diagonal
                  right edge), so the final label sits fully on slate with white
                  only below the diagonal. */}
              {isLastTrack && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 left-full w-32 bg-slate-50"
                  style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                />
              )}
              {/* Parallel diagonal dividers at the column boundaries (a
                  horizontal line rotated to match the labels, so it never
                  crosses them). The last column also closes its right
                  boundary. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 h-px w-[184px] origin-bottom-left -rotate-45 bg-slate-200"
              />
              {isLastTrack && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 h-px w-[184px] origin-bottom-left -rotate-45 bg-slate-200"
                  style={{ left: trackColumnWidth }}
                />
              )}
              {/* Label anchored over the column center so its base sits above
                  the data column. */}
              <Tooltip
                title={
                  <div>
                    <div className="font-semibold">{track.name}</div>
                    {typeLabel && (
                      <div className="text-xxs font-semibold text-emerald-300">
                        {typeLabel} · directly assignable
                      </div>
                    )}
                    {onTrackClick ? (
                      <div className="text-xxs opacity-80">
                        Click to assign to team members
                      </div>
                    ) : (
                      meta != null && (
                        <div className="text-xxs opacity-80">{meta}</div>
                      )
                    )}
                  </div>
                }
                arrow
              >
                <span
                  className={`absolute bottom-1 left-1/2 flex max-w-[150px] items-center gap-1 truncate pl-2.5 text-xs font-semibold text-slate-700 ${
                    onTrackClick
                      ? "cursor-pointer hover:text-violet-700"
                      : ""
                  }`}
                  style={{
                    transformOrigin: "bottom left",
                    // translate is listed first so it is applied in screen space
                    // (after the rotation): nudges the text down-right toward
                    // the centre of its lane.
                    transform: "translate(6px, 7px) rotate(-45deg)",
                  }}
                  {...clickableProps(track)}
                >
                  {TypeIcon && (
                    <TypeIcon
                      className="shrink-0 text-emerald-600"
                      style={{ fontSize: 14 }}
                    />
                  )}
                  {track.name}
                </span>
              </Tooltip>
            </div>
          </th>
        );
      })}
      {/* Filler column: the only auto-width column, so under table-fixed it
          soaks up whatever horizontal space is left after the fixed sticky and
          track columns — carrying the header slate and row borders to the full
          width without ever overflowing. (A percentage width here would resolve
          against the table width and blow the layout out.) */}
      <th
        aria-hidden="true"
        className={useHorizontalHeader ? "border-l border-slate-200" : ""}
      />
    </tr>
  );
}
